<template>
  <div>
    <div
      ref="reference"
      class="popper__wrapper"
    >
      <span
        :class="{'popper__trigger_active': isVisible}"
        class="popper__trigger"
        @click="onClick"
        @mouseover="onMouseOver"
        @mouseleave="onMouseLeave"
      >
        <slot name="reference" />
      </span>
    </div>
    <Portal>
      <transition
        name="popper-fade"
        @after-enter="wasOpened"
        @after-leave="wasClosed"
      >
        <div
          v-show="isVisible"
          :id="`#${popperId}`"
          ref="popper"
          :style="{ width: width }"
          :class="['popper', popperClass, { 'popper_with-arrow': visibleArrow, 'popper__in-modal': isInModal }]"
          @mouseover="onMousePopperOver"
          @mouseleave="onMousePopperLeave"
        >
          <slot name="popper" />
        </div>
      </transition>
      <transition name="popper-fade">
        <div
          v-show="isVisible"
          class="popper__shading-area"
        >
          <slot name="shading-area" />
        </div>
      </transition>
    </Portal>
  </div>
</template>

<script>
  import PopperJS from 'popper.js';
  import {Portal} from '@linusborg/vue-simple-portal';
  import nextFrameCreateObserver from 'utils/observers/nextFrameCreateObserver';

  /**
   * @param {HTMLElement} [reference=$slots.reference] - The reference element used to position the popper.
   * @param {HTMLElement} [popper=$slots.popper] - The HTML element used as popper,
   *         or a configuration used to generate the popper.
   * @param {HTMLElement} [shadingArea=$slots.shadingArea] - The reference element to shade background.
   * @param {String} [placement=bottom] - Placement of the popper accepted values:
   *         top(-start, -end), right(-start, -end), bottom(-start, -end), left(-start, -end).
   * @param {Number} [offset=0,10] - Amount of pixels the popper will be shifted (can be negative).
   * @param {Number} [padding=20] - Amount of pixels between border of reference element and popper (can be negative).
   * @param {Boolean} [visible=false] Visibility of the popup element.
   * @param {Boolean} [visible-arrow=false] Visibility of the arrow, no style.
   * @param {Boolean} [transition=true] Mark if popover uses transition.
   * @param {Boolean} [closeOnClickOutside=true] Close popover on click outside it.
   * @param {Boolean} [closeOnClickReference=true] Close popover on click on reference.
   * @param {Object} [options={}] Other popover-js options.
   * @param {Boolean} [fixWidth=false] Set fixed width to popover after render
   *
   * @emits 'created' - when popover creates new instance
   * @emits 'toggle' - when popover start toggling animation
   * @emits 'open' - when popover finished opening
   * @emits 'close' - when popover finished closing
   */
  export default {
    name: 'MarilynUIBasePopover',
    components: {Portal},
    model: {
      prop: 'visible',
      event: 'toggle'
    },
    props: {
      referenceElement: {
        type: HTMLElement,
        default: null
      },
      referenceSelector: {
        type: String,
        default: null
      },
      visible: Boolean,
      allowShow: {
        type: Boolean,
        default: true
      },
      placement: {
        type: String,
        default: 'bottom',
        validator: (val) => (/^(top|bottom|left|right)(-start|-end)?$/g).test(val)
      },
      padding: {
        type: [Number, String],
        default: 20
      },
      offset: {
        type: String,
        default: '0,6'
      },
      visibleArrow: Boolean,
      transition: {
        type: Boolean,
        default: true
      },
      closeOnClickOutside: {
        type: Boolean,
        default: true
      },
      closeOnClickReference: {
        type: Boolean,
        default: true
      },
      closeOnlyHoverPopperOutside: {
        type: Boolean,
        default: false
      },
      showOn: {
        type: String,
        choices: ['click', 'hover', 'none'],
        default: 'click'
      },
      options: {
        type: Object,
        default: Object
      },
      popperClass: {
        type: [String, Array, Object],
        default: ''
      },
      fixWidth: {
        type: Boolean,
        default: false
      },
      activateDelay: {
        type: Number,
        default: 0
      },
      popperId: {
        type: String,
        default: null
      },
      isInModal: {
        type: Boolean,
        default: false
      },
      gpuAcceleration: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {

        // variable for debounced show
        isVisibleDelayed: this.visible,
        isVisible: this.visible,
        width: 'auto'
      };
    },
    computed: {
      popperModifiers () {
        const defaultModifiers = {
          computeStyle: {
            gpuAcceleration: this.gpuAcceleration
          }
        };

        return {
          offset: {
            offset: this.offset,
            ...this.options.offset
          },
          preventOverflow: {
            padding: this.padding,
            ...(this.options.modifiers || {}).preventOverflow
          },
          ...defaultModifiers,
          ...this.options.modifiers
        };
      }
    },
    watch: {
      placement () {
        this.refreshPopper(this.isVisible);
      },
      isVisibleDelayed (val) {

        // no delay if disabling visible or delay is undefined
        if (!this.activateDelay || !val) {
          this.isVisible = val;
          return;
        }
        setTimeout(() => {
          if (!this.isVisibleDelayed) {
            return;
          }
          this.isVisible = val;
        }, this.activateDelay);
      },
      visible (val) {
        this.isVisible = val;
      },
      isVisible: {
        handler (val) {
          if (!this.allowShow) {
            return;
          }

          this.$emit('toggle', val);
          this.refreshPopper(val);

          // make width of popper fixed (after render) to prevent growing when content is added
          if (val && this.fixWidth) {
            this._$nextFrame(this.setFixedWidth);
          } else if (!val && this.fixWidth) {
            this.resetWidth();
          }
          this.isVisibleDelayed = val;
        },
        immediate: false
      }
    },
    async mounted () {
      const {observerDestructor} = await nextFrameCreateObserver(
        'MutationObserver',
        () => this.$refs.popper,
        this.updatePopperPosition,
        {childList: true, subtree: true}
      );

      this.$on('hook:beforeDestroy', () => {
        observerDestructor();
      });
    },

    beforeDestroy () {
      if (this.popperJS) {
        this.popperJS.destroy();
      }
    },
    created () {
      document.addEventListener('click', this.documentClick);
    },
    destroyed () {

      // important to clean up!!
      document.removeEventListener('click', this.documentClick);
    },
    methods: {
      onClick () {
        if (!this.closeOnClickReference && this.isVisible) {
          return;
        }

        if (this.showOn === 'click') {
          this.toggleVisible(!this.isVisible);
        }
      },
      onMousePopperOver () {
        if (!this.closeOnlyHoverPopperOutside || this.showOn !== 'hover') {
          return;
        }

        this.onMouseOver();
      },
      onMousePopperLeave () {
        if (!this.closeOnlyHoverPopperOutside || this.showOn !== 'hover') {
          return;
        }

        this.onMouseLeave();
      },
      onMouseOver () {
        if (this.showOn === 'hover' && !this.isVisible) {
          this.toggleVisible(true);
        }
      },
      onMouseLeave () {
        if (this.showOn === 'hover' && (this.isVisible || this.isVisibleDelayed)) {
          this.toggleVisible(false);
        }
      },
      getReference () {
        const referenceElement = this.referenceElement || this.$slots.reference && this.$refs.reference;
        if (this.referenceSelector) {
          return referenceElement.querySelector(this.referenceSelector);
        }
        return referenceElement;
      },
      setFixedWidth () {
        const initialWidth = this.$refs.popper.offsetWidth;
        this.width = `${initialWidth}px`;
      },

      resetWidth () {
        this.width = 'auto';
      },

      documentClick (e) {
        if (this.isVisible && this.closeOnClickOutside) {
          const el = this.$refs.popper;
          const trigger = this.getReference();
          const target = e.target;
          if (!trigger || el !== target && !el.contains(target) && trigger !== target && !trigger.contains(target)) {
            this.isVisible = false;
          }
        }
      },

      closeDropdown () {
        this.isVisible = false;
      },

      toggleVisible (isVisible) {
        this.isVisibleDelayed = isVisible;
        if (!isVisible) {
          this.isVisible = isVisible;
        }
      },

      createPopper () {
        if (!(/^(top|bottom|left|right)(-start|-end)?$/g).test(this.placement)) {
          return;
        }

        this.popper = this.$refs.popper;
        this.reference = this.getReference();

        if (!this.popper || !this.reference) {
          return;
        }

        if (this.visibleArrow) {
          this.appendArrow(this.popper);
        }

        this.popperJS = new PopperJS(
          this.reference,
          this.popper,
          {
            placement: this.placement,
            onCreate: () => {
              this.$emit('created', this);
            },
            ...this.options,
            modifiers: this.popperModifiers
          }
        );
      },

      updatePopperPosition () {
        if (this.popperJS) {
          this.popperJS.update();
        }
      },

      updatePopper () {
        if (this.popperJS) {
          this.doDestroy();
        }
        this.createPopper();
      },

      doDestroy () {
        this.popperJS.popper.removeEventListener('transitionend', this.doDestroy);
        this.popperJS.destroy();
        this.popperJS = null;
      },

      destroyPopper () {
        if (this.popperJS) {
          if (this.transition) {
            this.popperJS.popper.addEventListener('transitionend', this.doDestroy);
          } else {
            this.doDestroy();
          }
        }
      },

      refreshPopper (val) {
        this._$nextFrame(() => {
          if (val) {
            this.updatePopper();
          } else {
            this.destroyPopper();
          }
        });
      },

      appendArrow (element) {
        let hash;
        if (this.appended) {
          return;
        }

        this.appended = true;

        for (const item in element.attributes) {
          if ((/^_v-/).test(element.attributes[item].name)) {
            hash = element.attributes[item].name;
            break;
          }
        }

        const arrow = document.createElement('div');

        if (hash) {
          arrow.setAttribute(hash, '');
        }
        arrow.setAttribute('x-arrow', '');
        arrow.className = 'popper__arrow';
        element.appendChild(arrow);
      },

      wasOpened () {
        this.$emit('open');
      },
      wasClosed () {
        this.$emit('close');
      }
    }
  };
</script>

<style>
  .popper {
    --popover-arrow-size: 9px;
    --popover-arrow-offset: calc(calc(var(--popover-arrow-size) * -0.70710678118) + 0.5px);
    --popover-shading-z-index: 1000;
    --popper-box-shadow: 0px 3px 35px rgba(48, 56, 71, 0.3);

    font-family: var(--main-font-family);
    font-size: var(--main-font-size);
    font-weight: var(--main-font-weight);

    color: var(--text-color);
    line-height: var(--main-line-height);

    position: absolute;

    /*
      Fixes jumping scroll
      when popper element is created it adds to popper element to the end of page (end of doom)
      this hack places popper element on top of page
    */
    top: -1000px;
    left: 0;
    z-index: var(--popover-z-index);

    box-sizing: border-box;

    background-color: var(--background-color);
    box-shadow: var(--popper-box-shadow);

    border-radius: var(--border-radius);

    &.popper_with-arrow {
      margin: var(--popover-arrow-size);

      & .popper__arrow {
        position: absolute;

        box-sizing: border-box;
        width: calc(var(--popover-arrow-size) * 1.41421356237);
        height: calc(var(--popover-arrow-size) * 1.41421356237);

        background-color: var(--background-color);
        box-shadow: var(--popper-box-shadow);
      }

      &[x-placement^='top'] .popper__arrow {
        bottom: var(--popover-arrow-offset);
        left: calc(50% - var(--popover-arrow-size));

        transform: rotate(-45deg);
        clip-path: polygon(0 0, 0 100%, 100% 100%);
      }

      &[x-placement^='bottom'] .popper__arrow {
        top: var(--popover-arrow-offset);
        left: 50%;

        transform: rotate(45deg);
        clip-path: polygon(0 0, 0 100%, 100% 0);
      }

      &[x-placement^='right'] .popper__arrow {
        top: 50%;
        left: var(--popover-arrow-offset);

        transform: rotate(-45deg);
        clip-path: polygon(0 0, 0 100%, 100% 0);
      }

      &[x-placement^='left'] .popper__arrow {
        top: 50%;
        right: var(--popover-arrow-offset);

        transform: rotate(45deg);
        clip-path: polygon(0 0, 100% 0, 100% 100%);
      }
    }
  }

  .popper__shading-area {
    position: absolute;
    top: 0;
    z-index: var(--popover-shading-z-index);

    background-color: var(--shading-color);

    &:active,
    &:focus {
      outline: none;
    }
  }

  .popper__in-modal {
    z-index: 1101;
  }

  .popper-fade-leave-to,
  .popper-fade-enter {
    opacity: 0;
  }

  .popper-fade-enter-active,
  .popper-fade-leave-active {
    transition: opacity var(--transition-duration) ease-in-out;
  }
</style>
